"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { TrendingUp, Target, CircleCheck as CheckCircle2, ArrowRight, Building2, MapPin, ShoppingBag, ChartBar as BarChart3, ShieldCheck, Quote } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
interface CaseStudy {
  id: string;
  category: string;
  clientName: string;
  clientRole: string;
  company: string;
  location: string;
  avatar: string;
  iconName: string;
  headline: string;
  metrics: {
    label: string;
    value: string;
    detail: string;
  }[];
  challenge: string;
  strategy: string;
  implementation: string;
  verifiedResults: string;
  businessImpact: string;
  testimonial: string;
}
const caseStudiesData: CaseStudy[] = [{
  id: "local-seo",
  category: "Local SEO & Map Pack",
  clientName: "David Miller",
  clientRole: "Managing Director",
  company: "Wiltshire Legal Services",
  location: "Swindon, UK",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/client_david_miller.png",
  iconName: "MapPin",
  headline: "+184% Organic Visibility & Top 3 Local Map Rankings",
  metrics: [{
    label: "Organic Traffic",
    value: "+184%",
    detail: "Sustained 6-month growth"
  }, {
    label: "Top 3 Map Pack",
    value: "12 Keywords",
    detail: "High-intent legal queries"
  }, {
    label: "Consultation Calls",
    value: "+42%",
    detail: "Inbound qualified inquiries"
  }],
  challenge: "Outranked by generic national legal directories and local competitors due to inconsistent citations and non-optimized local landing pages.",
  strategy: "Conducted a thorough hyper-local SEO audit, overhauled Google Business Profile listings, and structured dedicated geo-targeted service clusters.",
  implementation: "Implemented transactional LegalService schema markup, cleaned up duplicate citations, and published hyper-local content tailored to Swindon & Wiltshire clients.",
  verifiedResults: "Achieved Top 3 positions in the Google Map Pack for core transactional terms within 120 days of execution.",
  businessImpact: "Direct consultation requests grew by 42%, allowing the firm to scale their client intake without increasing paid ad spend.",
  testimonial: "Yassine worked directly with us to fix our local search presence. The impact on incoming client inquiries was immediate and measurable."
}, {
  id: "b2b-seo",
  category: "B2B SEO & Lead Gen",
  clientName: "Elena Rostova",
  clientRole: "Head of Marketing",
  company: "Apex Enterprise Risk",
  location: "London, UK",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/client_elena_rostova.webp",
  iconName: "Building2",
  headline: "+230% Organic B2B Leads & £4,500/mo Ads Saved",
  metrics: [{
    label: "B2B Inbound Leads",
    value: "+230%",
    detail: "High-value pipeline growth"
  }, {
    label: "Non-Brand Search",
    value: "+145%",
    detail: "Top-of-funnel reach"
  }, {
    label: "PPC Cost Reduction",
    value: "£4.5k/mo",
    detail: "Reallocated to organic"
  }],
  challenge: "High dependency on costly Google Ads with low organic search coverage for complex, long-sales-cycle enterprise risk services.",
  strategy: "Mapped enterprise buying intent, streamlined technical crawling bottlenecks, and rebuilt bottom-of-funnel solution pages.",
  implementation: "Optimized Core Web Vitals, redesigned internal link architecture around high-value topics, and introduced decision-maker resource hubs.",
  verifiedResults: "Grew high-intent non-branded organic search impressions by 145% in 6 months while securing page 1 rankings for key B2B keywords.",
  businessImpact: "Cut monthly Google Ads expenditure by £4,500 while doubling the total qualified organic sales pipeline value.",
  testimonial: "Working directly with Yassine felt like having an in-house SEO director. He focuses on real revenue numbers, not just vanity search ranks."
}, {
  id: "ecom-seo",
  category: "Ecommerce SEO",
  clientName: "Marcus Vance",
  clientRole: "Founder & CEO",
  company: "Nordic Living Interiors",
  location: "National UK",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/client_marcus_vance.webp",
  iconName: "ShoppingBag",
  headline: "+310% Revenue from Search & 3.8x Investment ROI",
  metrics: [{
    label: "Organic Revenue",
    value: "+310%",
    detail: "Year-over-year revenue"
  }, {
    label: "Consultancy ROI",
    value: "3.8x",
    detail: "Measured net gain"
  }, {
    label: "Crawl Efficiency",
    value: "+85%",
    detail: "Faster indexation speed"
  }],
  challenge: "Organic revenue dropped after Google core updates due to duplicate category content and thin product descriptions.",
  strategy: "Implemented strict E-E-A-T signals, consolidated thin taxonomy pages, and resolved faceted navigation indexation issues.",
  implementation: "Created programmatic schema markup for product variants, improved site architecture speed, and optimized category copy for search intent.",
  verifiedResults: "Organic search became the company's #1 revenue channel within 6 months, generating a 3.8x return on consultancy investment.",
  businessImpact: "Expanded overall market share in the premium home goods niche with sustainable, non-paid organic buyer acquisition.",
  testimonial: "Yassine's technical knowledge and clear strategy transformed our online store's organic revenue. Highly recommend his hands-on service."
}];
export default function Impact() {
  const [activeTab, setActiveTab] = useState<string>("local-seo");
  const shouldReduceMotion = useReducedMotion();
  const activeCase = caseStudiesData.find(c => c.id === activeTab) || caseStudiesData[0];
  const getCategoryIcon = (iconName: string) => {
    switch (iconName) {
      case "MapPin":
        return <MapPin className="size-4" />;
      case "Building2":
        return <Building2 className="size-4" />;
      case "ShoppingBag":
        return <ShoppingBag className="size-4" />;
      default:
        return <BarChart3 className="size-4" />;
    }
  };
  return <section id="casestudyimpact" data-nav="light" className="relative bg-background py-20 lg:py-28 text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 right-1/4 -z-10 h-96 w-96 rounded-full bg-secondary/40 blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-10 -z-10 h-80 w-80 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5 text-primary px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            <ShieldCheck className="mr-1.5 size-3.5 text-primary" />
            Verified Case Studies
          </Badge>
          <h2 className="font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Real SEO Campaigns. Proven Business Impact.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            When you hire me as your SEO consultant, you work directly with me. No account managers, no automated template reports—just tailored strategies, hands-on implementation, and genuine revenue growth.
          </p>
        </div>

        {}
        <div className="mt-12 flex flex-wrap justify-center gap-3 sm:gap-4">
          {caseStudiesData.map((study, i) => {
          const isActive = study.id === activeTab;
          return <Button key={study.id} data-index={i} variant={isActive ? "default" : "outline"} size="lg" onClick={() => setActiveTab(study.id)} className={`transition-all duration-200 ${isActive ? "bg-primary text-primary-foreground shadow-md hover:bg-primary/90" : "bg-background text-foreground hover:bg-muted"}`}>
                {getCategoryIcon(study.iconName)}
                <span className="font-medium">{study.category}</span>
              </Button>;
        })}
        </div>

        {}
        <AnimatePresence mode="wait">
          <motion.div key={activeCase.id} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={shouldReduceMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          y: -16
        }} transition={{
          duration: 0.35,
          ease: "easeInOut"
        }} className="mt-10">
            <Card className="border-border bg-card shadow-xl overflow-hidden rounded-2xl">
              <CardContent className="p-6 sm:p-8 lg:p-10">
                
                {}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-b border-border pb-8">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground font-medium mb-2">
                      <span className="text-primary font-semibold">{activeCase.company}</span>
                      <span>•</span>
                      <span>{activeCase.location}</span>
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-foreground sm:text-3xl">
                      {activeCase.headline}
                    </h3>
                  </div>

                  <div className="flex items-center gap-4 shrink-0">
                    <Link to="/CaseStudiesPage">
                      <Button variant="outline" size="default" className="border-border hover:bg-accent font-medium">
                        View All Cases
                      </Button>
                    </Link>
                    <Link to="/ContactPage">
                      <Button variant="default" size="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
                        Book Consultation
                        <ArrowRight className="ml-2 size-4" />
                      </Button>
                    </Link>
                  </div>
                </div>

                {}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {activeCase.metrics.map((metric, i) => <div key={i} data-index={i} className="rounded-xl border border-border/80 bg-background/50 p-5 text-center sm:text-left transition-colors hover:border-primary/40">
                      <div className="text-3xl font-extrabold text-primary font-serif">
                        {metric.value}
                      </div>
                      <div className="text-sm font-semibold text-foreground mt-1">
                        {metric.label}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {metric.detail}
                      </div>
                    </div>)}
                </div>

                {}
                <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                  
                  {}
                  <div className="lg:col-span-7 space-y-6">
                    <h4 className="text-lg font-semibold text-foreground flex items-center gap-2 border-b border-border pb-3">
                      <Target className="size-5 text-primary" />
                      Campaign Breakdown & Execution
                    </h4>

                    {}
                    <div className="space-y-1.5">
                      <span className="inline-block text-xs font-bold uppercase tracking-wider text-muted-foreground bg-muted px-2 py-0.5 rounded">
                        1. The Challenge
                      </span>
                      <p className="text-sm text-foreground/90 leading-relaxed pl-1">
                        {activeCase.challenge}
                      </p>
                    </div>

                    {}
                    <div className="space-y-1.5">
                      <span className="inline-block text-xs font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded">
                        2. SEO Strategy
                      </span>
                      <p className="text-sm text-foreground/90 leading-relaxed pl-1">
                        {activeCase.strategy}
                      </p>
                    </div>

                    {}
                    <div className="space-y-1.5">
                      <span className="inline-block text-xs font-bold uppercase tracking-wider text-accent bg-accent/10 px-2 py-0.5 rounded">
                        3. Practical Implementation
                      </span>
                      <p className="text-sm text-foreground/90 leading-relaxed pl-1">
                        {activeCase.implementation}
                      </p>
                    </div>

                    {}
                    <div className="space-y-1.5">
                      <span className="inline-block text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded">
                        4. Verified Results
                      </span>
                      <p className="text-sm text-foreground/90 leading-relaxed pl-1">
                        {activeCase.verifiedResults}
                      </p>
                    </div>

                    {}
                    <div className="space-y-1.5 pt-2 border-t border-border/60">
                      <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                        <TrendingUp className="size-4" />
                        <span>Business Impact:</span>
                      </div>
                      <p className="text-sm font-medium text-foreground leading-relaxed pl-1">
                        {activeCase.businessImpact}
                      </p>
                    </div>
                  </div>

                  {}
                  <div className="lg:col-span-5 space-y-6">
                    {}
                    <div className="rounded-xl border border-border bg-muted/40 p-6 relative">
                      <Quote className="absolute top-4 right-4 size-8 text-muted-foreground/20" />
                      
                      <p className="text-sm italic leading-relaxed text-foreground/90 relative z-10">
                        &quot;{activeCase.testimonial}&quot;
                      </p>

                      <div className="mt-6 flex items-center gap-3 pt-4 border-t border-border/80">
                        <SafeHydrate><Avatar className="size-11 border border-primary/20">
                          <AvatarImage src={activeCase.avatar} alt={activeCase.clientName} />
                          <AvatarFallback className="bg-primary/10 text-primary font-bold">
                            {activeCase.clientName.slice(0, 2)}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        <div>
                          <div className="text-sm font-bold text-foreground">
                            {activeCase.clientName}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {activeCase.clientRole}, {activeCase.company}
                          </div>
                        </div>
                      </div>
                    </div>

                    {}
                    <div className="rounded-xl bg-primary/5 border border-primary/20 p-5 space-y-3">
                      <div className="flex items-center gap-2 font-semibold text-sm text-primary">
                        <CheckCircle2 className="size-4 shrink-0" />
                        <span>My Direct Consultant Guarantee</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        I handle every audit, technical fix, and content strategy personally. You will never be handed off to junior staff or account managers.
                      </p>
                    </div>

                    {}
                    <div className="pt-2">
                      <Link to="/ContactPage" className="w-full block">
                        <Button variant="default" size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                          Book Your Free Consultation
                          <ArrowRight className="ml-2 size-4" />
                        </Button>
                      </Link>
                    </div>

                  </div>

                </div>

              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        {}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            Want to see more verified SEO results across National, Local, and B2B sectors?{" "}
            <Link to="/CaseStudiesPage" className="text-primary font-semibold hover:underline inline-flex items-center gap-1">
              Browse the full Case Studies directory
              <ArrowRight className="size-3.5" />
            </Link>
          </p>
        </div>

      </div>
    </section>;
}